import { Card, Grid, Typography, Button, FormControlLabel, Checkbox, TextField, Box, Alert, AlertTitle } from '@mui/material';
import React, { useEffect, useState } from 'react'
import { Field, FormikProvider, useFormik } from 'formik';

import * as Yup from "yup";
import api from '../../../redux/features/auth/axiosAuthHelper';
import dayjs from 'dayjs';
import { TextEditField, TextEditReadOnlyField, FormDatePicker, TestTypeSelectField, TextEditFieldAdornment } from '../fields/fields';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import colorConfigs from '../../../configs/colorConfigs';
import { BatchSessionStorageKey, BatchTableParameterStorageService } from '../../../services/sessionStorage';
import RareButton from '../buttons/RareButton';
import { Batch } from '../types';
import InputAdornment from '@mui/material/InputAdornment';

interface BatchDetailProps {
    batch_id?: any | null;
    handleRefreshButton: () => void;
    handleBackButton: () => void;
}


const BatchDetailCard: React.FC<BatchDetailProps> = ({ batch_id, handleRefreshButton, handleBackButton }) => {
    const authData = useSelector((state: RootState) => state.auth);
    const [errors, setErrors] = useState<string | null>(null);
    const [initialValues, setInitialValues] = useState<any>({})
    const [updatedValues, setUpdatedValues] = useState<any>({})
    const [hasMadeChanges, setHasMadeChanges] = useState<boolean>(false)
    const [batch, setBatch] = useState<Batch>();

    const create = !batch_id;

    // Fetch batch data and point_model JSON data
    useEffect(() => {
        if (batch_id) {
            const fetchBatch = async () => {
                try {
                    const url = `batches/${batch_id}`;

                    const response = await api.get(url, {
                        headers: {
                            'Authorization': `Bearer ${authData.access}`
                        }
                    });
                    let fetcheddata = {
                        id: response.data.id,
                        lot_number: response.data.lot_number,
                        test_type: response.data.test_type,
                        control_line_threshold: response.data.control_line_threshold,
                        threshold1: response.data.threshold1,
                        threshold2: response.data.threshold2 ? response.data.threshold2 : null,
                        threshold_percentage: parseFloat(
                            (response.data.threshold_percentage * 100).toFixed(2)
                        ),
                        expiry_date: response.data.expiry_date,
                        is_active: response.data.is_active
                    }

                    setBatch(fetcheddata as Batch);
                } catch (error) {
                    console.error('Error fetching batch', error);
                    setErrors(`No Batch found with ID ${batch_id}`)
                    setTimeout(() => {
                        handleBackButton()
                    }, 4000);
                }
            };
            fetchBatch();
        }
    }, [batch_id]);

    const validationSchema = Yup.object().shape({
        expiry_date: Yup.date()
            .typeError('Expiry Date must be a valid date')
            .required('Expiry Date is required'),
        // control_line_threshold: Yup.number()
        //     .typeError('Control Line Threshold must be a number')
        //     .min(0, 'Control Line Threshold must be at least 0')
        //     .max(255, 'Control Line Threshold must be at most 255')
        //     .required('Control Line Threshold is required'),

        // threshold1: Yup.number()
        //     .typeError('Threshold 1 must be a number')
        //     .min(0, 'Threshold 1 must be at least 0.0')
        //     .max(1, 'Threshold 1 must be at most 1.0')
        //     .required('Threshold 1 is required'),

        // threshold2: Yup.number()
        //     .typeError('Threshold 2 must be a number')
        //     .min(0, 'Threshold 2 must be at least 0.0')
        //     .max(1, 'Threshold 2 must be at most 1.0')
        //     .when('test_type', {
        //         is: 'Oncho',
        //         then: schema =>
        //             schema.required('Threshold 2 is required for Oncho tests'),
        //         otherwise: schema => schema.notRequired().nullable(),
        //     }),

        // threshold_percentage: Yup.number()
        //     .typeError('Threshold Percentage must be a number')
        //     .min(1, 'Threshold Percentage must be at least 1')
        //     .max(100, 'Threshold Percentage must be at most 100')
        //     .required('Threshold Percentage is required'),
    });

    const formik = useFormik({
        enableReinitialize: !create,
        validateOnChange: false,
        validateOnBlur: false,
        initialValues:
            (!create && batch) ? {
                id: batch.id,
                lot_number: batch.lot_number,
                expiry_date: dayjs(batch.expiry_date).format('YYYY-MM-DD'),
                is_active: batch.is_active,
                control_line_threshold: batch.control_line_threshold,
                threshold1: batch.threshold1,
                threshold2: batch.threshold2,
                test_type: batch.test_type,
                threshold_percentage: batch.threshold_percentage,
            }
                : {
                    id: '',
                    lot_number: '',
                    expiry_date: '',
                    is_active: true,
                    control_line_threshold: null,
                    threshold1: null,
                    threshold2: null,
                    test_type: '',
                    threshold_percentage: null,
                },
        validationSchema,
        onSubmit: async (values) => {

            const formattedExpiryDate = values.expiry_date ? dayjs(values.expiry_date).format('YYYY-MM-DD') : null;

            let percentage = values.threshold_percentage ? values.threshold_percentage / 100 : 0

            const data = authData.isDDTDAdmin ? {
                lot_number: values.lot_number,
                is_active: values.is_active,
                expiry_date: formattedExpiryDate,
                control_line_threshold: values.control_line_threshold,
                threshold1: values.threshold1,
                threshold2: formik.values.test_type === 'Oncho' ? values.threshold2 : null,
                test_type: values.test_type,
                threshold_percentage: percentage,
            } : {

            }

            const url = batch ? `batches/${batch.id}/` : `batches/`
            const method = batch ? 'patch' : 'post'

            try {
                console.log("data", data)
                await api({
                    method,
                    url,
                    data,
                    headers: {
                        'Authorization': `Bearer ${authData.access}`,
                    },
                });
                setErrors(null);
                console.log("Batch successfully updated")
                handleRefreshButton() // reset states and close popup
                const count = BatchTableParameterStorageService.get(BatchSessionStorageKey.count)
                BatchTableParameterStorageService.set(BatchSessionStorageKey.count, count + 1) // Updated count to not trigger refresh data button
            } catch (error: any) {
                console.log("error", error)
                if (error?.response?.status === 400 && error.response.data) {
                    const data = error.response.data;
                    // If you want to show field‐level errors under each input:
                    const fieldErrors: Record<string, string> = {};
                    Object.entries(data).forEach(([field, messages]) => {
                        // DRF usually returns an array of strings per field
                        fieldErrors[field] = Array.isArray(messages)
                            ? messages.join(' ')
                            : String(messages);
                    });
                    formik.setErrors(fieldErrors);

                    // Optionally also show a summary at the top
                    const summary = Object.entries(fieldErrors)
                        .map(([f, msg]) => `${f}: ${msg}`)
                        .join(' — ');
                    setErrors(summary);
                } else if (error?.response?.status === 403) {
                    const firstErrorItem = Object.keys(error.response.data)[0];
                    setErrors(`${error.response.data[firstErrorItem]}`);
                } else {
                    console.error("Unknown error updating lot", error);
                    setErrors("Unknown error updating lot");
                }
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        }
    });


    // Effect to set initial form values from batch to check for form changes
    useEffect(() => {
        setTimeout(() => {
            setInitialValues(formik.initialValues);
            setUpdatedValues(formik.initialValues);
        }, 500);
        setHasMadeChanges(false);
    }, [batch]);

    // Update the updatedValues state when formik values change
    useEffect(() => {
        setUpdatedValues(formik.values);
    }, [formik.values]);

    // Function to check if form has changed so the save button becomes active on edit
    const hasFormChanged = (flatModel: Record<string, any>, formValues: Record<string, any>): boolean => {
        for (const key in formValues) {
            if (key === "expiry_date") {
                const formDate = dayjs(formValues[key]).format('YYYY-MM-DD');
                const initialDate = create ? dayjs(flatModel[key]).format('YYYY-MM-DD') : dayjs(formValues[key]).format('YYYY-MM-DD');
                if (formDate !== initialDate) {
                    console.log("Changed Date", formDate, initialDate)
                    return true;
                }
            } else {
                if (formValues[key] != flatModel[key]) {
                    console.log("Changed Data", formValues[key], flatModel[key])
                    return true;
                }
            }
        }
        return false;
    };

    // Use Effect to check if any changes ahve been made to the form on Edit
    useEffect(() => {
        if (!create && batch) {
            if (Object.keys(initialValues).length > 0 && Object.keys(updatedValues).length > 0) {
                const formChanged = hasFormChanged(initialValues, updatedValues);
                setHasMadeChanges(formChanged);
            }
        }
    }, [updatedValues, initialValues, batch]);

    // Use Effect set save button active on load if Create and if changes are made on Edit
    useEffect(() => {
        if (create) {
            setHasMadeChanges(true);
        } else {
            setHasMadeChanges(false);
        }
    }, [create]);

    return (
        <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit} style={{ overflow: 'auto' }}>
                <Box sx={{ height: "100%", p: 2, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                    {errors && (
                        <Alert severity="error" style={{ width: "59.5%" }}>
                            <AlertTitle>Error</AlertTitle>
                            {errors}
                        </Alert>
                    )}
                    <div style={{ width: "100%", height: "100%", margin: "auto", padding: "1em", display: "flex", alignItems: "center" }}>
                        <Grid container spacing={5} alignItems="top" justifyContent="center" style={{ minWidth: "100%", padding: 5 }}>
                            <Grid item xs={12} md={5} style={{ display: "flex", flexDirection: "column", }}>
                                <Card sx={{ pb: 1, boxShadow: 10, height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex' }}>
                                        <Box sx={{ height: '100%', background: colorConfigs.tables.headBg, display: 'flex', alignItems: 'center', borderBottomRightRadius: 3 }}>
                                            <Typography sx={{ px: 2, py: 0.5, color: '#fff', fontWeight: 'bold', fontSize: '20px' }}>Batch Details</Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ p: 2, pt: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
                                        {!create && <Field name="id" disableds type="text" className="form-control" label="Batch ID" component={TextEditReadOnlyField} />}
                                        <Field name="lot_number" type="text" className="form-control" label="Lot Number" component={TextEditField} />
                                        <Field name="test_type" type="text" className="form-control" label="Test Type" component={!authData.isDDTDAdmin ? TextEditReadOnlyField : TestTypeSelectField} />
                                        <Field
                                            name="expiry_date"
                                            type="text"
                                            className="form-control"
                                            label="Expiry Date"
                                            component={FormDatePicker}
                                        />
                                        <FormControlLabel
                                            sx={{ mt: 0.5 }}
                                            control={
                                                <Checkbox
                                                    checked={formik.values.is_active}
                                                    name="is_active"
                                                    sx={{
                                                        '&.Mui-checked': {
                                                            color: colorConfigs.buttons.active,
                                                        },
                                                    }}
                                                    disabled={!authData.isDDTDAdmin}
                                                    onChange={formik.handleChange}
                                                    className="form-control"
                                                />
                                            }
                                            label={
                                                <Box display="flex" alignItems="center">
                                                    Batch is
                                                    <Typography
                                                        fontSize="smaller"
                                                        sx={{
                                                            paddingX: 0.7,
                                                            border: formik.values.is_active
                                                                ? `2px solid ${colorConfigs.buttons.active}`
                                                                : `1px solid ${colorConfigs.buttons.inactive}`,
                                                            borderRadius: 2,
                                                            color: formik.values.is_active
                                                                ? colorConfigs.buttons.active
                                                                : colorConfigs.buttons.inactive,
                                                            marginLeft: 1,
                                                        }}
                                                    >
                                                        {formik.values.is_active ? 'Active' : 'Inactive'}
                                                    </Typography>
                                                </Box>
                                            }
                                        />

                                    </Box>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={5} style={{ display: "flex", flexDirection: "column", }}>
                                <Card sx={{ pb: 2, boxShadow: 10, height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex' }}>
                                        <Box sx={{ height: '100%', background: colorConfigs.tables.headBg, display: 'flex', alignItems: 'center', borderBottomRightRadius: 3 }}>
                                            <Typography sx={{ px: 2, py: 0.5, color: '#fff', fontWeight: 'bold', fontSize: '20px' }}>Batch Thresholds</Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ pl: 2, pr: 2, pt: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
                                        <Field name="control_line_threshold" type="number" className="form-control" label="Control Line Threshold" helperText={"Luminosity, range: 0 (Perfectly Black) - 255 (Perfectly White)"} component={TextEditField} />
                                        {/* <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}> */}
                                        <Field name="threshold1" type="number" className="form-control" label="Threshold 1" helperText={"T/C ratio, range: 0.0 - 1.0"} component={TextEditField} />
                                        {/* <Typography>&#62;</Typography>                          */}
                                        {formik.values.test_type === 'Oncho' &&
                                            <Field name="threshold2" type="number" className="form-control" label="Threshold 2" helperText={"T/C ratio, range: 0.0 - 1.0"} component={TextEditField} />}
                                        {/* </Box> */}
                                        <Field
                                            name="threshold_percentage"
                                            type="number"
                                            component={TextEditFieldAdornment}
                                            label="Threshold Percentage"
                                            adornment="%"
                                            adornmentPosition="end"
                                            helperText="Range: 1% - 100%"
                                        />
                                    </Box>
                                </Card>
                            </Grid>

                        </Grid>
                    </div>
                    <div className="form-group" style={{ width: "100%", display: 'flex', alignItems: 'center', justifyContent: "space-between" }}>
                        <RareButton
                            onClick={() => handleBackButton()}
                            variant="outlined"
                            sx={{
                                position: "relative",
                                m: 1,
                            }}
                        >
                            Back
                        </RareButton>
                        <RareButton
                            type="submit"
                            variant="success"
                            // disabled={!hasMadeChanges}
                            sx={{
                                position: "relative",
                                m: 1,
                            }}
                        >
                            Save Changes
                        </RareButton>
                    </div>
                </Box>
            </form>
        </FormikProvider>
    )
}

export default BatchDetailCard;